import React, { useRef, useEffect } from 'react';
import { Flex, TextInput, ScreenReaderContent } from '@smarterservices/smarter-ui';
import { getOrdinal } from '@session/helpers/helpers';

/**
 * VerificationInput component for code verification inputs
 * 
 * @param {Object} props - Component props
 * @param {number} props.length - Number of input fields to display (default: 4)
 * @param {Function} props.onChange - Callback when input value changes
 * @param {string} props.value - Current input value
 * @param {Function} props.onComplete - Callback when all fields are filled
 * @param {'digits'|'alpha'|'alphanumeric'} props.characterType - Type of characters allowed (default: 'digits')
 * @param {number} props.splitAfter - Insert splitText after this many characters (optional)
 * @param {string} props.splitText - Text to insert between groups of inputs (optional)
 */
export const VerificationInput = ({
  length = 4,
  onChange,
  value,
  onComplete,
  characterType = 'digits',
  splitAfter,
  splitText,
}) => {
  const inputRefs = useRef(Array(length).fill().map(() => ({ current: null })));
  
  // Initialize refs on mount and when length changes
  useEffect(() => {
    inputRefs.current = Array(length).fill().map((_, i) => ({
      current: inputRefs.current[i]?.current || null
    }));
  }, [length]);

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputRefs.current[index - 1].current.focus();
    }
  };

  // Define regex pattern based on characterType prop
  const getRegexPattern = () => {
    switch (characterType) {
      case 'digits':
        return /^\d*$/;
      case 'alpha':
        return /^[a-zA-Z]*$/;
      case 'alphanumeric':
        return /^[a-zA-Z0-9]*$/;
      default:
        return /^\d*$/;
    }
  };

  const handleChange = (e, index) => {
    const newValue = e.target.value.toUpperCase(); // Convert to uppercase for better readability
    if (newValue.length <= 1 && getRegexPattern().test(newValue)) {
      const newInputValue = value ? value.split('') : Array(length).fill('');
      newInputValue[index] = newValue;
      const finalValue = newInputValue.join('');
      onChange(finalValue);
      // Move to next input if value is entered
      if (newValue.length === 1 && index < length - 1) {
        inputRefs.current[index + 1]?.current?.focus();
      }

      // Only trigger onComplete when we're on the last input AND have a valid digit
      if (index === length - 1 && newValue.length === 1) {
        // Use setTimeout to ensure state has updated
        setTimeout(() => {
          onComplete(finalValue);
        }, 0);
      }
    }
  };



  // Determine if we should show splits between groups
  const shouldShowSplits = splitAfter && splitText && Number.isInteger(splitAfter) && splitAfter > 0;

  return (
    <Flex direction="row" gap="small" justifyContent="center" wrap="wrap">
      {Array(length)
        .fill()
        .map((_, index) => {
          // Determine if we need to add a split after this input
          const showSplit = shouldShowSplits && 
                          (index + 1) % splitAfter === 0 && 
                          index !== length - 1;
          
          return (
            <React.Fragment key={index}>
              <TextInput
                renderLabel={
                  <ScreenReaderContent>
                    {`${getOrdinal(index + 1)} ${characterType === 'digits' ? 'digit' : 'character'} of verification code`}
                  </ScreenReaderContent>
                }
                display="inline-block"
                width="3rem"
                size="large"
                textAlign="center"
                autoComplete="off"
                inputRef={(el) => { inputRefs.current[index].current = el; }}
                value={value[index] || ''}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                pattern={characterType === 'digits' ? '[0-9]*' : characterType === 'alpha' ? '[a-zA-Z]*' : '[a-zA-Z0-9]*'}
                inputMode={characterType === 'digits' ? 'numeric' : 'text'}
                onFocus={(e) => e.target.select()}
              />
              
              {/* Add split text if needed */}
              {showSplit && (
                <Flex as="span" alignItems="center" padding="xx-small" aria-hidden="true">
                  {splitText}
                </Flex>
              )}
            </React.Fragment>
          );
        })}
    </Flex>
  );
};

{/*
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            ref={inputRefs[index]}
            value={value[index] || ''}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            style={{
              width: '40px',
              height: '48px',
              fontSize: '24px',
              textAlign: 'center',
              margin: '0 4px',
              border: 'none',
              borderBottom: '2px solid #ccc',
              outline: 'none',
              borderRadius: '4px',
            }}
          />
           */}
